/**
 * The transport the API layer talks to. In the real app this is the Socket.IO
 * connection (net/socket.ts); the DEV-only mock (dev/mockRoom.ts) swaps in an
 * in-memory backend so every screen can be rendered without a server.
 */
import type { Activity } from '@wordloom/engine';

export interface Backend {
  /**
   * Emit `event` (with `payload` unless it is undefined) and resolve with the
   * server's ack. Rejects when no ack arrives within `timeoutMs`.
   */
  emit(event: string, payload: unknown, timeoutMs: number): Promise<unknown>;
  /** Fire-and-forget activity signal (no ack). */
  sendActivity(activity: Activity): void;
  /** Start the transport if it is not running yet (the socket connects lazily). */
  connect(): void;
}

let current: Backend | null = null;
let connectedHook: (() => void) | null = null;

/**
 * Hook run after EVERY successful (re)connection of the transport. The session
 * layer (net/session.ts) uses it to re-join the room with the stored seat token.
 */
export function setOnConnected(fn: (() => void) | null): void {
  connectedHook = fn;
}

/** Called by a transport once it is connected. */
export function notifyConnected(): void {
  connectedHook?.();
}
let fallbackFactory: (() => Backend) | null = null;

/** Registers the lazily-created default backend (the socket). */
export function setDefaultBackendFactory(factory: () => Backend): void {
  fallbackFactory = factory;
}

export function setBackend(backend: Backend | null): void {
  current = backend;
}

export function getBackend(): Backend {
  if (!current) {
    if (!fallbackFactory) throw new Error('No backend configured');
    current = fallbackFactory();
  }
  return current;
}
